/** Shared interface pieces. */
import type { ComponentChildren, JSX } from 'preact';
import { useEffect } from 'preact/hooks';

// --------------------------------------------------------------------------
// The graduated meter — the app's signature device.
// --------------------------------------------------------------------------

export function Meter(props: {
  label: string;
  value: number;
  goal?: number | undefined;
  unit?: string;
  mini?: boolean;
  /** Treat filling the bar as success rather than as spend. */
  goodWhenFull?: boolean;
}): JSX.Element {
  const { label, value, goal, unit = '', mini, goodWhenFull } = props;
  const hasGoal = typeof goal === 'number' && goal > 0;
  const pct = hasGoal ? Math.min(100, (value / goal) * 100) : 0;
  const over = hasGoal && value > goal;
  const near = hasGoal && !over && pct >= 90;
  const fillClass = over ? 'is-over' : (goodWhenFull && near) || (goodWhenFull && pct >= 100) ? 'is-good' : '';

  return (
    <div class={`meter${mini ? ' meter-mini' : ''}`}>
      <div class="row-between">
        <span class="label">{label}</span>
        <span class="unit">
          {fmt(value)}{unit}
          {hasGoal ? <span class="dim"> / {fmt(goal)}{unit}</span> : null}
        </span>
      </div>
      <div
        class="meter-track"
        role="meter"
        aria-label={label}
        aria-valuenow={Math.round(value)}
        aria-valuemin={0}
        aria-valuemax={hasGoal ? Math.round(goal) : undefined}
      >
        {hasGoal ? <div class={`meter-fill ${fillClass}`} style={{ width: `${pct}%` }} /> : null}
      </div>
    </div>
  );
}

/** Numbers that read cleanly at a glance: no trailing noise. */
export function fmt(n: number | undefined | null, dp = 0): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return '—';
  if (dp === 0) return String(Math.round(n));
  return String(Number(n.toFixed(dp)));
}

// --------------------------------------------------------------------------

export function Stat(props: { label: string; value: string; unit?: string; sub?: string }): JSX.Element {
  const empty = props.value === '—';
  return (
    <div class={`stat${empty ? ' is-empty' : ''}`}>
      <span class="label">{props.label}</span>
      <span class="stat-val">
        <span class="readout readout-md">{props.value}</span>
        {props.unit && !empty ? <span class="unit">{props.unit}</span> : null}
      </span>
      {props.sub ? <span class="label" style={{ fontSize: '9px' }}>{props.sub}</span> : null}
    </div>
  );
}

export function Card(props: { title?: string; action?: ComponentChildren; children: ComponentChildren; flush?: boolean }): JSX.Element {
  return (
    <section class={`card${props.flush ? ' card-flush' : ''}`}>
      {props.title || props.action ? (
        <header class="card-head" style={props.flush ? { padding: '14px 14px 0' } : undefined}>
          {props.title ? <h2 class="label label-lit" style={{ margin: 0 }}>{props.title}</h2> : <span />}
          {props.action}
        </header>
      ) : null}
      {props.children}
    </section>
  );
}

export function Empty(props: { children: ComponentChildren; mark?: ComponentChildren }): JSX.Element {
  return (
    <div class="empty">
      {props.mark ? <div class="empty-mark">{props.mark}</div> : null}
      <p>{props.children}</p>
    </div>
  );
}

// --------------------------------------------------------------------------
// Sheet: how every logging flow opens.
// --------------------------------------------------------------------------

export function Sheet(props: {
  title: string;
  onClose: () => void;
  children: ComponentChildren;
  footer?: ComponentChildren;
}): JSX.Element {
  // A sheet must not leave the page scrolling behind it.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') props.onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [props.onClose]);

  return (
    <>
      <div class="sheet-scrim" onClick={props.onClose} />
      <div class="sheet" role="dialog" aria-modal="true" aria-label={props.title}>
        <div class="grip" />
        <header class="sheet-head">
          <h2 class="label label-lit" style={{ margin: 0 }}>{props.title}</h2>
          <button class="btn btn-sm btn-ghost" onClick={props.onClose}>Close</button>
        </header>
        <div class="sheet-body">{props.children}</div>
        {props.footer ? <div class="sheet-foot">{props.footer}</div> : null}
      </div>
    </>
  );
}

// --------------------------------------------------------------------------

export function Stepper(props: {
  value: number; onChange: (v: number) => void; step?: number; min?: number; max?: number; dp?: number;
}): JSX.Element {
  const step = props.step ?? 1;
  const dp = props.dp ?? 0;
  const clamp = (v: number) => Math.min(props.max ?? Infinity, Math.max(props.min ?? -Infinity, v));
  return (
    <div class="stepper">
      <button type="button" aria-label="Decrease" onClick={() => props.onChange(clamp(Number((props.value - step).toFixed(6))))}>−</button>
      <input
        class="input input-lg"
        type="text"
        inputMode="decimal"
        value={props.value.toFixed(dp)}
        onInput={(e) => {
          const v = Number((e.target as HTMLInputElement).value);
          if (Number.isFinite(v)) props.onChange(clamp(v));
        }}
      />
      <button type="button" aria-label="Increase" onClick={() => props.onChange(clamp(Number((props.value + step).toFixed(6))))}>+</button>
    </div>
  );
}

export function Scale10(props: { value?: number | undefined; onChange: (v: number) => void }): JSX.Element {
  return (
    <div class="scale10">
      {Array.from({ length: 11 }, (_, i) => (
        <button
          type="button"
          key={i}
          class={props.value === i ? 'is-on' : ''}
          aria-pressed={props.value === i}
          onClick={() => props.onChange(i)}
        >{i}</button>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------

export function Sparkline(props: { values: (number | null | undefined)[]; color?: string }): JSX.Element | null {
  const pts = props.values.map((v, i) => ({ i, v: typeof v === 'number' ? v : null }));
  const real = pts.filter((p) => p.v !== null) as { i: number; v: number }[];
  if (real.length < 2) return null;

  const min = Math.min(...real.map((p) => p.v));
  const max = Math.max(...real.map((p) => p.v));
  const span = max - min || 1;
  const w = 100;
  const h = 30;
  const x = (i: number) => (i / Math.max(1, pts.length - 1)) * w;
  const y = (v: number) => h - ((v - min) / span) * h;

  // Break the line across gaps rather than drawing through missing days.
  let d = '';
  let pen = false;
  for (const p of pts) {
    if (p.v === null) { pen = false; continue; }
    d += `${pen ? 'L' : 'M'}${x(p.i).toFixed(1)},${y(p.v).toFixed(1)} `;
    pen = true;
  }

  return (
    <svg class="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={d.trim()} stroke={props.color ?? 'var(--coral)'} />
    </svg>
  );
}

// --------------------------------------------------------------------------

export function Toast(props: { text: string; error?: boolean }): JSX.Element {
  return <div class={`toast${props.error ? ' is-error' : ''}`} role="status">{props.text}</div>;
}

export function Field(props: { label: string; children: ComponentChildren; hint?: string }): JSX.Element {
  return (
    <label class="field">
      <span class="label">{props.label}</span>
      {props.children}
      {props.hint ? <span class="label" style={{ textTransform: 'none', letterSpacing: 0, fontSize: '11px' }}>{props.hint}</span> : null}
    </label>
  );
}

// --------------------------------------------------------------------------
// Icons — single stroke weight, drawn to match the hairline borders.
// --------------------------------------------------------------------------

const ic = (d: string) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);

export const Icons = {
  today: ic('M3 10h18M7 3v3M17 3v3M5 6h14a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z'),
  food: ic('M6 3v8a3 3 0 0 0 6 0V3M9 11v10M17 3c-1.5 2-2 4-2 6s.5 3 2 3 2-1 2-3-.5-4-2-6ZM17 12v9'),
  train: ic('M4 9v6M8 7v10M16 7v10M20 9v6M8 12h8'),
  pin: ic('M14 4l6 6M17 7l-9 9-4 1 1-4 9-9M11 13l-2-2'),
  more: ic('M4 7h16M4 12h16M4 17h16'),
  plus: ic('M12 5v14M5 12h14'),
  run: ic('M13 4a1 1 0 1 0 0-.01M10 21l2-5-2-3 1-5 3 2 3 1M6 12l3-1M8 21l2-4'),
};
